import { useEffect, useState } from "react";
import { Box, Button, FormControl, InputLabel, List, ListItem, MenuItem, Select, Tooltip } from "@mui/material";
import FormFieldBuilder from "./FormFieldBuilder";
import { FormField, IncludesEntity, RuleCategory } from "generated/graphql";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  addNewRule,
  addSelectedRules,
  selectAllDescriptions,
  selectCountsByDescription,
  selectCountsByField,
  selectCountsBySubtype,
  selectCurrentField,
  selectDraftMode,
  selectLensEditorForm,
  selectLensEditorLens,
  selectMatchingDescriptions,
  selectMatchingSubtypes,
  selectSelectedFieldRules,
  selectSelectedRuleTypeComponents,
  setSelectedField,
  setSelectedRuleTypeComponents,
} from "../../redux/slices/lensEditor";
import { nonEmpty } from "../../helpers/coll";
import { Add } from "@mui/icons-material";
import { RuleMode } from "routes/lens-editor/RuleMode";
import { validateAddRule } from "Backend";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";

export interface RulesEditorProps {
  field?: FormField;
}

const RulesEditor = (props: RulesEditorProps) => {
  const { searchParams, updateParam } = useSearchParamsWithUpdate();
  const currentFieldId = searchParams.get("profileFieldId")
    ? parseInt(searchParams.get("profileFieldId")!, 10)
    : undefined;
  const { field } = props;
  const dispatch = useAppDispatch();
  const [showFieldDetail] = useState(false);
  const allDescriptions = useAppSelector(selectAllDescriptions);
  const fieldRules = useAppSelector(selectSelectedFieldRules);
  const draftMode = useAppSelector(selectDraftMode);
  const countsBySubtype = useAppSelector(selectCountsBySubtype);
  const countsByDescription = useAppSelector(selectCountsByDescription);
  const renderSubtypeChoice = (subtype: string) => `${subtype} (${countsBySubtype.get(subtype) || 0})`;
  const renderDescriptionChoice = (desc: string) => `${desc} (${countsByDescription.get(desc) || 0})`;
  const { category, subtypes, descriptions } = useAppSelector(selectSelectedRuleTypeComponents);
  const matchingSubtypes = useAppSelector(selectMatchingSubtypes);
  const matchingDescriptions = useAppSelector(selectMatchingDescriptions);
  const lens = useAppSelector(selectLensEditorLens);
  const lensId = lens.id;
  const countsByField = useAppSelector(selectCountsByField);
  const currentField = useAppSelector(selectCurrentField);
  const form = useAppSelector(selectLensEditorForm);

  const makeNewRule = async (entityQuery?: IncludesEntity, textQuery?: string, fieldId?: number) => {
    dispatch(addNewRule({ lensId, entityQuery, textQuery, fieldId }));
  };
  const selectFieldById = (fieldId: number | undefined) => {
    dispatch(setSelectedField(fieldId ? form.fields?.find((f) => f.id === fieldId) : undefined));
    updateParam("profileFieldId", fieldId?.toString());
  };

  useEffect(() => {
    selectFieldById(currentFieldId);
  }, [currentFieldId, form.fields]);

  const addRuleValid = validateAddRule({
    draftMode,
    field: currentField,
    subtypes,
    descriptions,
  });

  return (
    <Box sx={{ width: "100%" }}>
      {showFieldDetail && field && (
        <FormFieldBuilder state={field} setState={() => void 0} lensRuleDescriptions={allDescriptions} />
      )}
      <Box sx={{ display: "flex", flexDirection: "column", mt: 1, gap: 1, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
          }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="profile-field-select-label">Profile Field</InputLabel>
            <Select
              size="small"
              labelId="profile-field-select-label"
              id="profile-field-select"
              value={currentField?.id || "no-field"}
              label="Profile Field"
              onChange={(event) =>
                selectFieldById(event.target.value !== "no-field" ? parseInt(`${event.target.value}`, 10) : undefined)
              }>
              {(form.fields ?? []).map((field, index) => (
                <MenuItem key={index} value={field.id}>
                  {field.name} ({countsByField.get(field.id) || 0})
                </MenuItem>
              ))}
              <MenuItem value="no-field">No Field ({countsByField.get(null) || 0})</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="category1-select-label">Category</InputLabel>
            <Select
              size="small"
              labelId="category1-select-label"
              id="category1-select"
              value={category}
              label="Category"
              onChange={(s) =>
                dispatch(
                  setSelectedRuleTypeComponents({ category: s.target.value as RuleCategory, subtypes, descriptions })
                )
              }>
              <MenuItem value={RuleCategory.Product}>{RuleCategory.Product}</MenuItem>
              <MenuItem value={RuleCategory.Injury}>{RuleCategory.Injury}</MenuItem>
              <MenuItem value={RuleCategory.Other}>{RuleCategory.Other}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
          }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="category2-select-label">Subtype</InputLabel>
            <Select
              size="small"
              labelId="category2-select-label"
              id="category2-select"
              value={subtypes}
              label="Subtype"
              multiple
              onChange={(s) => {
                dispatch(
                  setSelectedRuleTypeComponents({
                    category,
                    subtypes: s.target.value as string[],
                    descriptions: undefined,
                  })
                );
              }}>
              {matchingSubtypes.map((st, index) => (
                <MenuItem key={index} value={st}>
                  {renderSubtypeChoice(st)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="category3-select-label">Description</InputLabel>
            <Select
              size="small"
              labelId="category3-select-label"
              id="category3-select"
              value={descriptions}
              label="Description"
              multiple
              onChange={(d) => {
                if (d.target.value.includes("select_all")) {
                  dispatch(setSelectedRuleTypeComponents({ category, subtypes, descriptions: matchingDescriptions }));
                  return;
                }
                dispatch(
                  setSelectedRuleTypeComponents({ category, subtypes, descriptions: d.target.value as string[] })
                );
              }}>
              {matchingDescriptions.length > 1 && <MenuItem value="select_all">Select All</MenuItem>}
              {matchingDescriptions.map((d, index) => (
                <MenuItem key={index} value={d}>
                  {renderDescriptionChoice(d)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {(nonEmpty(fieldRules) || field) && (
        <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 1, paddingLeft: "7px" }}>
          {nonEmpty(fieldRules) && (
            <Button
              size="small"
              color="default"
              onClick={() => dispatch(addSelectedRules({ ruleIds: fieldRules.map((r) => r.ruleId) }))}>
              Select All
            </Button>
          )}
          {!nonEmpty(fieldRules) && <div></div>}
          <Tooltip title={addRuleValid.message}>
            <span>
              <Button
                color="default"
                size="small"
                disabled={!addRuleValid.allowAdd}
                onClick={() => makeNewRule(undefined, "text", field?.id)}
                startIcon={<Add />}>
                Add rule
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      <List sx={{ overflow: "auto" }}>
        {fieldRules.map((rule) => {
          return (
            <ListItem key={rule.ruleId} divider>
              <RuleMode rule={rule} />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default RulesEditor;
